import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { OnboardingStatus, SelfRegisteredOrg } from "@/api/types";

// ---- Active org (browser-session-side filter) ----

export function useSetActiveOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { slug: string | null }) => {
      const r = await api.post<{ slug: string | null; is_set: boolean }>(
        "/auth/active-org",
        payload,
      );
      return r.data;
    },
    onSuccess: () => {
      // The backend now sees a new ``active_org_id`` for this
      // browser; every list/aggregation that scopes by it has to
      // refetch or the SPA paints stale cross-tenant data until a
      // page navigation kicks off a fresh fetch.
      //
      // Endpoint → query key mapping:
      //   /auth/me                         keys.me
      //   /api/sessions                    keys.sessions
      //   /api/sessions/archived           ["sessions", "archived"]
      //   /api/documents                   ["documents", "library"]
      //   /api/orgs                        ["orgs"]
      //   /api/admin/costs/summary         ["admin", "costs", "summary"]
      //   /api/admin/costs/users/…         ["admin", "costs", "user"]
      //   /api/admin/costs/sessions/…      ["admin", "costs", "session"]
      //
      // Brand preview is per-org URL'd already so its cache key is
      // org-scoped and survives a switch.
      qc.invalidateQueries({ queryKey: keys.me });
      qc.invalidateQueries({ queryKey: keys.sessions });
      qc.invalidateQueries({ queryKey: ["sessions", "archived"] });
      qc.invalidateQueries({ queryKey: ["documents", "library"] });
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["admin", "costs"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}


// ---- Orgs (CRUD + memberships) ----

export type OrgView = {
  id: string;
  slug: string;
  name: string;
  is_root: boolean;
  created_at: string;
  membership_role: "member" | "manager" | "admin" | null;
};

export type OrgMemberView = {
  user_id: string;
  email: string;
  display_name: string;
  role: "member" | "manager" | "admin";
  joined_at: string;
  source: "manual" | "invitation" | "sso_rule" | "root_env" | "unknown";
};

const orgsKey = ["orgs"] as const;
const deletedOrgsKey = ["orgs", "deleted"] as const;
const orgMembersKey = (slug: string) =>
  ["orgs", slug, "members"] as const;
const invitationsKey = (slug: string) =>
  ["orgs", slug, "sso", "invitations"] as const;

export function useOrgs() {
  return useQuery({
    queryKey: orgsKey,
    queryFn: async () => (await api.get<OrgView[]>("/orgs")).data,
    staleTime: 30_000,
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { slug: string; name: string }) =>
      (await api.post<OrgView>("/orgs", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgsKey });
    },
  });
}


// ---- Self-serve onboarding (/app/welcome) ----

const onboardingStatusKey = ["onboarding", "status"] as const;

export function useOnboardingStatus() {
  return useQuery({
    queryKey: onboardingStatusKey,
    queryFn: async () =>
      (await api.get<OnboardingStatus>("/onboarding/status")).data,
    staleTime: 30_000,
  });
}

export function useSelfRegisterOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string;
      slug: string;
      /** Where the legal documents are published (spec §8.2): the
       *  creator accepts the Terms and the DPA for the org, at the
       *  versions the form showed. */
      accept_terms?: boolean;
      accept_dpa?: boolean;
      terms_version?: number;
      dpa_version?: number;
      ui_language?: string;
    }) =>
      (await api.post<SelfRegisteredOrg>("/onboarding/org", payload)).data,
    onSuccess: () => {
      // The backend pinned the new org as the session's active org —
      // me/orgs and the onboarding state are all stale now.
      qc.invalidateQueries({ queryKey: keys.me });
      qc.invalidateQueries({ queryKey: orgsKey });
      qc.invalidateQueries({ queryKey: onboardingStatusKey });
    },
  });
}

export function useUpdateOrg(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string }) =>
      (await api.patch<OrgView>(`/orgs/${slug}`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgsKey });
    },
  });
}

export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      await api.delete(`/orgs/${slug}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgsKey });
      qc.invalidateQueries({ queryKey: deletedOrgsKey });
    },
  });
}

export type DeletedOrgView = {
  id: string;
  slug: string;
  name: string;
  deleted_at: string;
  deleted_by_user_id: string | null;
  deleted_by_email: string | null;
  balance_micros: number;
  balance_credits: number;
};

/** Superadmin: tombstoned orgs retained for accounting + inspection. */
export function useDeletedOrgs() {
  return useQuery({
    queryKey: deletedOrgsKey,
    queryFn: async () =>
      (await api.get<DeletedOrgView[]>("/orgs/deleted")).data,
    staleTime: 30_000,
  });
}

/** Superadmin: restore a tombstoned org (same identity + retained ledger). */
export function useReopenOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) =>
      (await api.post<OrgView>(`/orgs/${slug}/reopen`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgsKey });
      qc.invalidateQueries({ queryKey: deletedOrgsKey });
    },
  });
}

export function useOrgMembers(slug: string) {
  return useQuery({
    queryKey: orgMembersKey(slug),
    queryFn: async () =>
      (await api.get<OrgMemberView[]>(`/orgs/${slug}/members`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

/** Whether the viewer may give the admin role in this org to recover it:
 *  true only for a superadmin in a customer org with no active admin (the
 *  server's ``org_without_active_admin``; a disabled admin still lists as
 *  ``admin``, so the members list can't tell). Keyed under the members
 *  key, so every member change refreshes it. */
export function useOrgAdminRecovery(slug: string) {
  return useQuery({
    queryKey: [...orgMembersKey(slug), "admin-recovery"] as const,
    queryFn: async () =>
      (await api.get<{ admin_recovery: boolean }>(`/orgs/${slug}/admin-recovery`)).data
        .admin_recovery,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

/** The add-member answer when the address could not be added directly
 *  (no account, or one the actor shares no org with): the backend sent an
 *  invitation instead, with one indistinguishable 201 for both cases. */
export type OrgMemberInvitedView = {
  status: "invited";
  email: string;
  role: string;
  invitation_link: string;
};

export function useAddOrgMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload:
        | { user_id: string; role: string }
        | { email: string; role: string },
    ) =>
      (
        await api.post<OrgMemberView | OrgMemberInvitedView>(
          `/orgs/${slug}/members`,
          payload,
        )
      ).data,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: orgMembersKey(slug) });
      if ("status" in result && result.status === "invited") {
        qc.invalidateQueries({ queryKey: invitationsKey(slug) });
      }
    },
  });
}

export function useUpdateOrgMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { user_id: string; role: string }) =>
      (
        await api.patch<OrgMemberView>(
          `/orgs/${slug}/members/${payload.user_id}`,
          { role: payload.role },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgMembersKey(slug) });
    },
  });
}

export function useRemoveOrgMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (user_id: string) => {
      await api.delete(`/orgs/${slug}/members/${user_id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgMembersKey(slug) });
    },
  });
}


// ---- Invitations ---------------------------------------------------

export type InvitationView = {
  id: string;
  org_id: string;
  email: string;
  role: "member" | "manager" | "admin";
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  link: string | null;
};


export function useInvitations(slug: string) {
  return useQuery({
    queryKey: invitationsKey(slug),
    queryFn: async () =>
      (await api.get<InvitationView[]>(`/orgs/${slug}/invitations`)).data,
    enabled: Boolean(slug),
    staleTime: 15_000,
  });
}

export function useCreateInvitation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; role: string }) =>
      (await api.post<InvitationView>(`/orgs/${slug}/invitations`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey(slug) }),
  });
}

export function useRevokeInvitation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/orgs/${slug}/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey(slug) }),
  });
}

/** Re-send a pending invitation — rotates its token + refreshes the expiry. */
export function useResendInvitation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<InvitationView>(
          `/orgs/${slug}/invitations/${id}/resend`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationsKey(slug) }),
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: async (token: string) =>
      (
        await api.post<{ org_slug: string; role: string }>(
          "/invitations/accept",
          { token },
        )
      ).data,
  });
}
